import type { VariantResource } from "../../variant";
import type { PriceListResource } from "../../price-list";
import type { CustomerResource } from "../../customer";
import type { OwnerResource } from "../../owner";
import type { OwnerProfileResource } from "../../owner-profile";
import type { AddressResource, MediaResource } from "../../../schemas.ts";
import type { PaymentMethodResource } from "../../payment-method";
import type { ProductGroupResource } from "../../product-group";
import type { CustomerGroupResource } from "../../customer-group";
import type { CountryResource } from "../../country";
import type { VoucherResource } from "../../voucher";
import type { ProductResource } from "../../product";
import type { MarketplaceResource } from "../../marketplace";
import type { DocumentResource } from "../../document";
import type {
  ContactResource,
  EmailContactResource,
  PhoneContactResource,
} from "../../contact/schemas";
import type { CurrencyResource } from "../../currency";
import type { WarehouseResource } from "../../warehouse";
import type { UserResource } from "../../user";

export interface DeliveryResource {
  id: string;
  title: string;
  is_active: boolean;
  sort: number;
  is_not_orderprocessing: boolean;
  is_not_free_vat: boolean;
  delivery_services?: DeliveryServiceResource[];
}

export interface TakeOverCustomerRequest {
  id?: string;
  first_name: string | null;
  last_name: string | null;
  emails?: {
    contact_type?: string;
    contact_subtype?: string;
    contact_value: string;
  }[];
  phones?: {
    contact_type?: string;
    contact_subtype?: string;
    contact_value: string;
  }[];
  price_list_id?: string | null;
  customer_groups?: string[] | null;
  birth_date: string | null;
  vat_number?: string;
  company?: string;
  contacts?: {
    contact_subtype?: string;
    contact_value: string;
    contact_type?: string;
  }[];
  zip?: string;
  city?: string;
  address_line_1?: string;
  country?: string;
  address_line_2?: string;
  salutation?: string;
}

export interface OrderResource {
  id: string;
  number: string;
  order_date: string;
  customer_id: string | null;
  owner_id: string | null;
  owner_profile_id: string | null;
  billing_address_id: string | null;
  delivery_address_id: string | null;
  payment_id: string | null;
  delivery_id: string | null;
  vat_number: string | null;
  billing_email: EmailContactResource | null;
  delivery_email: EmailContactResource | null;
  billing_phones: PhoneContactResource[] | null;
  delivery_phones: PhoneContactResource[] | null;
  currency_id: string | null;
  currency?: CurrencyResource;
  status: string;
  is_net_mode: boolean;
  marketplace_id: string | null;

  customer?: CustomerResource;
  owner?: OwnerResource;
  owner_profile?: OwnerProfileResource;
  billing_address?: AddressResource;
  delivery_address?: AddressResource;
  payment?: PaymentMethodResource;
  delivery?: DeliveryResource;
  order_positions?: OrderPositionResource[];
  order_payment_detail?: OrderPaymentDetailResource;
  order_delivery_services?: OrderDeliveryServiceResource[];
  order_summary?: OrderSummaryResource[];
  marketplace?: MarketplaceResource;
  documents?: DocumentResource[];
  shipping_service_provider: string | null;
  tracking_number: string | null;

  marketplace_order_number: string;
}

export interface OrderDeliveryServiceResource {
  id: string;
  order_id: string | null;
  delivery_service_id: string | null;
  tracking_number: string;
  send_date: string | null;
  delivery_service?: DeliveryServiceResource;
  order_delivery_service_positions?: OrderDeliveryServicePositionResource[];
  label_file?: MediaResource | null;
  customs_doc_file?: MediaResource | null;
  entnahmeschein?: MediaResource | null;
  delivery_note_id: string | null;
  delivery_note?: DocumentResource | null;
}

export interface OrderPaymentDetailResource {
  id: string;
  order_id: string | null;
  payment_details: string;
}
export interface OrderSummaryResource {
  id: string;
  order_id: string | null;
  type: string;
  vat: number;
  amount: number;
}

export interface DeliveryServiceResource {
  id: string;
  gid: string;
  title: string;
  is_active: boolean;
  sort: number;
  is_cod_allowed: boolean;
  carrier_id: string;
  carrier: string;
  carrier_params: Record<string, unknown>;
  deliveries?: DeliveryResource[];
  deliveries_count?: number;

  excluded_variants?: VariantResource[];
  excluded_variants_count: number;
  included_variants?: VariantResource[];
  included_variants_count: number;

  excluded_products?: ProductResource[];
  excluded_products_count: number;
  included_products?: ProductResource[];
  included_products_count: number;

  excluded_product_groups?: ProductGroupResource[];
  excluded_product_groups_count: number;
  included_product_groups?: ProductGroupResource[];
  included_product_groups_count: number;

  excluded_customer_groups?: CustomerGroupResource[];
  excluded_customer_groups_count: number;
  included_customer_groups?: CustomerGroupResource[];
  included_customer_groups_count: number;

  excluded_countries?: CountryResource[];
  excluded_countries_count: number;
  included_countries?: CountryResource[];
  included_countries_count: number;

  excluded_customers?: CustomerResource[];
  excluded_customers_count: number;
  included_customers?: CustomerResource[];
  included_customers_count: number;
}

export interface OrderPositionResource {
  id: string;
  order_id: string | null;
  variant_id: string | null;
  variant_ean: string;
  variant_sku: string;
  variant_title: string;
  option_value: string;
  quantity: string;
  unit_price: string;
  price_list_id: string | null;

  vat_percent: string;
  is_canceled: boolean;
  voucher_amount: string;
  total_price: string;
  variant?: VariantResource;
  price_list?: PriceListResource;
  order_position_components?: OrderPositionComponentResource[];
  order_delivery_service_positions?: OrderDeliveryServicePositionResource[];
}

export interface OrderPositionComponentResource {
  id: string;
  order_position_id: string | null;
  order_id: string | null;
  variant_id: string | null;
  component_variant_id: string | null;
  variant_ean: string;
  variant_sku: string;
  variant_title: string;
  option_value: string;
  quantity: number;
  unit_price: number;
  price_list_id: string | null;
  unit_discount_amount: number;
  unit_price_discounted: number;
  total_price_discounted: number;
  vat_percent: number;
  voucher_amount: number;
  total_price: number;
  purchasing_price: number;
  variant?: VariantResource;
  component_variant?: VariantResource;
  price_list?: PriceListResource;
}

export interface OrderDeliveryServicePositionResource {
  id: string;
  order_delivery_service_id: string | null;
  order_position_id: string | null;
  variant_id: string | null;
  component_variant_id: string | null;
  order_delivery_service?: OrderDeliveryServiceResource;
  order_position?: OrderPositionResource;
  variant?: VariantResource;
  component_variant?: VariantResource;
  quantity: string;
  purchasing_unit_price: string;
}

export interface OrderItem {
  id: string;
  gid?: string;
  sku?: string;
  title: string;
  price: string;
  total?: string;
  image?: string;
  remove_item?: string;
  variant?: VariantResource;
}

export interface OrderItemWithQuantity extends OrderItem {
  amount: number;
  vats?: number[];
  voucher_discounts?: { [voucher_id: string]: number };
  voucher_discounts_sum: number;
}

export interface VatBreakdown {
  price: number;
  vat: number;
  isNet: boolean;
  vatAmount: number;
  netPrice: number;
  grossPrice: number;
}

export interface OrderAdminDiscount {
  grossPrice: number;
  isNet: boolean;
  netPrice: number;
  price: number;
  vat: number;
  vatAmount: number;
}

export interface OrderDeliveryCosts {
  grossPrice: number;
  isNet: boolean;
  netPrice: number;
  price: number;
  vat: number;
  vatAmount: number;
}

export interface OrderPaymentCosts {
  grossPrice: number;
  isNet: boolean;
  netPrice: number;
  price: number;
  vat: number;
  vatAmount: number;
}

export interface OrderBasketTotals {
  product_total: number;
  product_totals: [] | null;
  voucher_discounts: {
    [vouched_id: string]: { vat: number; value: number }[] | number;
  };
  voucher_discounts_sum: number;
  voucher_discounts_vats: number | null;
  admin_discount: number;
  admin_discount_vats: number | null;
  delivery_cost: number;
  delivery_cost_vats: number | null;
  payment_cost: number;
  payment_cost_vats: number | null;
  total_nets: VatBreakdown[];
  total_net: number;
  total_gross: number;
}

export interface OrderBasketResponse {
  items: OrderItemWithQuantity[];
  customer: CustomerResource;
  billing_address: string | null;
  billing_email: ContactResource | null;
  billing_phones: ContactResource[] | null;
  delivery_email: EmailContactResource | null;
  delivery_phones: PhoneContactResource[] | null;
  delivery_address: AddressResource | null;
  currency: string;
  delivery_price: number;
  admin_discount: number;
  totals: OrderBasketTotals;
  is_net: boolean;
  order_remark: string | null;
  delivery_id?: string;
  payment_id?: string;
  voucher_discounts: any[];
  vouchers: { [voucher_id: string]: VoucherResource };
  errors: any[];
  deliveries: {
    id: string;
    price: number;
    title: string;
  }[];
  payments: { id: string; title: string }[];
  marketplace_id: string | null;
}

export interface OrderAddressResource {
  billing_address: {
    salutation: string | null;
    first_name: string | null;
    last_name: string | null;
    company: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    zip: string | null;
    city: string | null;
    country_id: string | null;
  };
  delivery_address: {
    salutation: string | null;
    first_name: string | null;
    last_name: string | null;
    company: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    zip: string | null;
    city: string | null;
    country_id: string | null;
  };
  billing_phones: Partial<PhoneContactResource>[];
  billing_email: Partial<EmailContactResource>;
  delivery_phones: Partial<PhoneContactResource>[];
  delivery_email: Partial<EmailContactResource>;
}

export type StoreOrderRequestBody = {
  customer_id?: number | null;
};

export type UpdateOrderRequestBody = {
  customer_id?: number | null;
};

export type PicklistItemResource = {
  id: string;
  order_delivery_service_id: string;
  order_delivery_service_position_id: string;
  variant_id: string;
  quantity_required: string;
  quantity_scanned: string;
  quantity_packaged: string;
  basket_number: string | null;
  variant?: VariantResource;
};

export enum PicklistTypeEnum {
  Complex = "complex",
  Simple = "simple",
}
export enum PicklistStatusEnum {
  Creating = "creating",
  Ready = "ready",
  PreCommissioning = "pre_commissioning",
  PreCommissioningDone = "pre_commissioning_done",
  Picking = "picking",
  Picked = "picked",
  Packing = "packing",
  Finished = "finished",
  CancelledManual = "cancelled_manual",
  CancelledError = "cancelled_error",
}

export type PicklistResource = {
  id: string;
  warehouse_id: string;
  number: string;
  created_by: string;
  status: PicklistStatusEnum;
  type: PicklistTypeEnum;
  user?: UserResource;
  user_id?: string;
  options: any | null;
  finished_at: string | null;
  cancelled_at: string | null;
  created_at: string | null;
  order_delivery_services?: OrderDeliveryServiceResource[];
  warehouse?: WarehouseResource;
  items?: PicklistItemResource[];
};

export type BoardOrderShipmentItem = {
  id: string;
  sku: string;
  title: string;
  quantity_required: string;
  quantity_scanned: string;
  quantity_packed: string;
};

export type BoardOrderShipment = {
  id: string;
  tracking_number: string;

  label_required: boolean;
  labels_scanned: number;

  processing_status: OrderProcessingStatusEnum;
  packed_by: null | string;
  packed_at: null | string;
  items: BoardOrderShipmentItem[];
};

export enum BoardColumnEnum {
  Unprocessed = "unprocessed",
  InDistribution = "in_distribution",
  Distributed = "distributed",
  InPacking = "in_packing",
  Packed = "packed",
}

export enum PrintStatusEnum {
  Queued = "queued",
  Printed = "printed",
  Failed = "failed",
}

export enum OrderProcessingStatusEnum {
  Pending = "pending",
  Picked = "picked",
  Sent = "sent",
}

export type BoardOrder = {
  id: string;
  number: string | null;
  board_column: BoardColumnEnum;
  basket_number: number;

  picking_started_at: string | null;
  articles_scanned: string;
  articles_required: string;
  articles_packed: string;
  documents_scanned: number;
  print_document_status: PrintStatusEnum | null;
  print_document_error: string | null;
  print_label_status: PrintStatusEnum | null;
  print_label_error: string | null;
  shipments: BoardOrderShipment[];
};

export type PicklistBoardResource = {
  id: string;
  number: string;
  options: Record<string, any>;
  status: PicklistStatusEnum;
  type: PicklistTypeEnum;
  board_counts: {
    unprocessed: number;
    distributed: number;
    in_distribution: number;
    packed: number;
    in_packing: number;
  };
  orders: BoardOrder[];
  cancelled_at: string | null;
};
